import React, { useState, useEffect, useMemo } from 'react';
import { 
  Calendar as CalendarIcon, 
  CalendarDays, 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Filter, 
  Sparkles, 
  LogIn, 
  CheckCircle2,
  Layers,
  ArrowRight
} from 'lucide-react';
import MonthView from './MonthView';
import WeekView from './WeekView';
import DayView from './DayView';
import DayOverviewModal from './DayOverviewModal';
import { useAuth } from '../../context/AuthContext';
import { 
  formatFullDate, 
  formatDateIso, 
  getScheduleForDate, 
  getWeekDays,
  getCalendarPreferences,
  saveCalendarPreferences
} from '../../utils/calendarUtils';

export default function CalendarHub({
  protocols = [],
  isDemo = false,
  onOpenAuth = () => {},
  currentUser: propUser
}) {
  const authContext = useAuth ? useAuth() : null;
  const currentUser = propUser !== undefined ? propUser : authContext?.currentUser;
  const userId = currentUser?.uid;

  // Initialize from persisted preferences (defaults to Sun–Sat, Month view)
  const initialPrefs = useMemo(() => getCalendarPreferences(userId), [userId]);

  const [viewMode, setViewMode] = useState(() => initialPrefs.viewMode); // 'month' | 'week' | 'day'
  const [weekStart, setWeekStart] = useState(() => initialPrefs.weekStart); // 'sun' | 'mon'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  // Sync state if authenticated user logs in or switches
  useEffect(() => {
    if (userId) {
      const prefs = getCalendarPreferences(userId);
      setViewMode(prefs.viewMode);
      setWeekStart(prefs.weekStart);
    }
  }, [userId]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('Active'); // 'Active' | 'Paused' | 'Pending' | 'All'
  const [categoryFilter, setCategoryFilter] = useState('All'); // 'All' | 'Supplement' | 'Peptide' | 'Hormones & AAS'
  const [searchQuery, setSearchQuery] = useState('');

  // View Mode Change Handler (persists default for user)
  const handleViewModeChange = (newMode) => {
    setViewMode(newMode);
    saveCalendarPreferences(userId, { viewMode: newMode });
  };

  // Week Start Toggle Handler (persists default for user)
  const handleWeekStartChange = (newWeekStart) => {
    setWeekStart(newWeekStart);
    saveCalendarPreferences(userId, { weekStart: newWeekStart });
  };

  // Date Navigation handlers
  const handleNavigate = (direction) => {
    setCurrentDate(prev => {
      const next = new Date(prev);
      if (viewMode === 'month') {
        next.setMonth(next.getMonth() + direction);
      } else if (viewMode === 'week') {
        next.setDate(next.getDate() + (direction * 7));
      } else {
        next.setDate(next.getDate() + direction);
      }
      return next;
    });
  };

  const handleJumpToToday = () => {
    setCurrentDate(new Date());
  };

  // Compute period title
  const periodTitle = useMemo(() => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    }
    if (viewMode === 'week') {
      const weekDays = getWeekDays(currentDate, weekStart);
      const start = weekDays[0].date;
      const end = weekDays[6].date;
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const year = end.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${start.getDate()} – ${end.getDate()}, ${year}`;
      }
      return `${startMonth} ${start.getDate()} – ${endMonth} ${end.getDate()}, ${year}`;
    }
    return formatFullDate(currentDate);
  }, [viewMode, currentDate, weekStart]);

  // Schedule for selected modal day
  const selectedDaySchedule = useMemo(() => {
    if (!selectedDay) return null;
    return getScheduleForDate(protocols, selectedDay, {
      statusFilter,
      categoryFilter
    });
  }, [selectedDay, protocols, statusFilter, categoryFilter]);

  return (
    <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-6">
      
      {/* Demo Notice Banner for unauthenticated visitors */}
      {isDemo && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-sky-950/60 via-indigo-950/40 to-slate-900 border border-sky-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-lg shadow-sky-950/20">
          <div className="flex items-center space-x-3">
            <span className="p-2 rounded-xl bg-sky-500/20 text-sky-400 border border-sky-500/30">
              <Sparkles className="w-5 h-5" />
            </span>
            <div>
              <h4 className="text-sm font-bold text-white flex items-center space-x-2">
                <span>Interactive Calendar Preview</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/20 text-sky-300 border border-sky-500/30">
                  Sample Stack (14 Compounds)
                </span>
              </h4>
              <p className="text-xs text-slate-400 mt-0.5">
                Explore the daily scheduling engine. Sign in to sync your personal protocols across devices.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onOpenAuth}
            className="flex items-center space-x-1.5 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold text-xs transition shadow-md shadow-sky-500/20 shrink-0"
          >
            <LogIn className="w-3.5 h-3.5" />
            <span>Sign In to Save</span>
          </button>
        </div>
      )}

      {/* Main Calendar Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 border-b border-slate-800/80 pb-6">
        <div>
          <div className="flex items-center space-x-2 text-xs font-semibold text-sky-400 uppercase tracking-widest mb-1.5">
            <CalendarIcon className="w-4 h-4" />
            <span>Stack Administration Timetable</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
            Regimen Calendar
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Track daily compound administration across morning, midday, and bedtime schedule windows.
          </p>
        </div>

        {/* View Layout Switcher & Week Start Toggle */}
        <div className="flex flex-wrap items-center gap-2.5 self-start lg:self-auto shrink-0">
          {/* Week Start Day Switcher */}
          <div 
            className="flex items-center space-x-1 bg-slate-900/90 p-1 rounded-xl border border-slate-800 shrink-0 shadow-inner"
            role="group"
            aria-label="Week Start Day"
          >
            <button
              type="button"
              onClick={() => handleWeekStartChange('sun')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition ${
                weekStart === 'sun'
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title="Start week on Sunday (Sun–Sat)"
              aria-pressed={weekStart === 'sun'}
            >
              Start Sun
            </button>

            <button
              type="button"
              onClick={() => handleWeekStartChange('mon')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition ${
                weekStart === 'mon'
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title="Start week on Monday (Mon–Sun)"
              aria-pressed={weekStart === 'mon'}
            >
              Start Mon
            </button>
          </div>

          {/* View Layout Switcher (Day, Week, Month) */}
          <div 
            className="flex items-center space-x-1 bg-slate-900 p-1.5 rounded-2xl border border-slate-800 shrink-0 shadow-inner"
            role="group"
            aria-label="Calendar View Layout"
          >
            <button
              type="button"
              onClick={() => handleViewModeChange('day')}
              className={`flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
                viewMode === 'day'
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              aria-pressed={viewMode === 'day'}
            >
              <Clock className="w-3.5 h-3.5" />
              <span>Day</span>
            </button>

            <button
              type="button"
              onClick={() => handleViewModeChange('week')}
              className={`flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
                viewMode === 'week'
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              aria-pressed={viewMode === 'week'}
            >
              <CalendarDays className="w-3.5 h-3.5" />
              <span>Week</span>
            </button>

            <button
              type="button"
              onClick={() => handleViewModeChange('month')}
              className={`flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition ${
                viewMode === 'month'
                  ? 'bg-sky-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              aria-pressed={viewMode === 'month'}
            >
              <CalendarIcon className="w-3.5 h-3.5" />
              <span>Month</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Toolbar & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-2xl bg-slate-900/60 border border-slate-800/90 shadow-sm">
        
        {/* Date Stepper Controls */}
        <div className="flex items-center space-x-2.5">
          <div className="flex items-center space-x-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
            <button
              type="button"
              onClick={() => handleNavigate(-1)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              title="Previous Period"
              aria-label="Previous Period"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => handleNavigate(1)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
              title="Next Period"
              aria-label="Next Period"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={handleJumpToToday}
            className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs border border-slate-700 transition"
          >
            Today
          </button>

          <h2 className="text-base sm:text-lg font-bold text-white pl-2">
            {periodTitle}
          </h2>
        </div>

        {/* Filter Controls: Category, Status & Search */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category Dropdown */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 text-xs font-medium focus:outline-none focus:border-sky-500 cursor-pointer"
          >
            <option value="All">All Categories</option>
            <option value="Supplement">Supplements</option>
            <option value="Peptide">Peptides</option>
            <option value="Hormones & AAS">Hormones & AAS</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 text-xs font-medium focus:outline-none focus:border-sky-500 cursor-pointer"
          >
            <option value="Active">Active Only</option>
            <option value="Pending">Pending Only</option>
            <option value="Paused">Paused Only</option>
            <option value="All">All Statuses</option>
          </select>

          {/* Search Input */}
          <div className="relative min-w-[160px] sm:min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter compound..."
              className="w-full pl-8 pr-3 py-1.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 text-xs focus:outline-none focus:border-sky-500 transition"
            />
          </div>
        </div>

      </div>

      {/* Active Calendar View */}
      {viewMode === 'month' && (
        <MonthView
          currentDate={currentDate}
          protocols={protocols}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          searchQuery={searchQuery}
          weekStart={weekStart}
          onSelectDay={(day) => setSelectedDay(day)}
        />
      )}

      {viewMode === 'week' && (
        <WeekView
          currentDate={currentDate}
          protocols={protocols}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          searchQuery={searchQuery}
          weekStart={weekStart}
          onSelectDay={(day) => setSelectedDay(day)}
        />
      )}

      {viewMode === 'day' && (
        <DayView
          currentDate={currentDate}
          protocols={protocols}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          searchQuery={searchQuery}
          onSelectDay={(day) => setSelectedDay(day)}
        />
      )}

      {/* Day Overview Modal */}
      <DayOverviewModal
        isOpen={Boolean(selectedDay)}
        onClose={() => setSelectedDay(null)}
        selectedDate={selectedDay}
        schedule={selectedDaySchedule}
        onNavigateDate={(delta) => {
          setSelectedDay(prev => {
            const next = new Date(prev);
            next.setDate(next.getDate() + delta);
            return next;
          });
        }}
      />

    </main>
  );
}
